/**
 * Die Sitzung: wer angemeldet ist, und die Anmeldung selbst.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  api,
  beiSitzungsende,
  gespeichertesMitglied,
  speichereSitzung,
  verwerfeSitzung,
  type GespeichertesMitglied,
} from './client.js'

interface SitzungsZustand {
  mitglied: GespeichertesMitglied | null
  meldeAn: (token: string, mitglied: GespeichertesMitglied) => void
  meldeAb: () => void
}

const SitzungsKontext = createContext<SitzungsZustand | null>(null)

export function SitzungsProvider({ children }: { children: ReactNode }) {
  const [mitglied, setMitglied] = useState<GespeichertesMitglied | null>(gespeichertesMitglied)

  useEffect(() => {
    beiSitzungsende(() => setMitglied(null))
  }, [])

  // Beim Kaltstart prüfen, ob das gespeicherte Token noch gilt.
  useEffect(() => {
    if (!mitglied) return
    api.get<GespeichertesMitglied>('/api/auth/ich').catch(() => {
      // 401 räumt der Client selbst ab; alles andere lässt die Sitzung stehen.
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const meldeAn = useCallback((token: string, wer: GespeichertesMitglied) => {
    speichereSitzung(token, wer)
    setMitglied(wer)
  }, [])

  const meldeAb = useCallback(() => {
    verwerfeSitzung()
    setMitglied(null)
  }, [])

  const wert = useMemo(() => ({ mitglied, meldeAn, meldeAb }), [mitglied, meldeAn, meldeAb])
  return <SitzungsKontext.Provider value={wert}>{children}</SitzungsKontext.Provider>
}

export function useSitzung(): SitzungsZustand {
  const kontext = useContext(SitzungsKontext)
  if (!kontext) throw new Error('useSitzung braucht den SitzungsProvider.')
  return kontext
}
